import {
    normalizeWidgetTheme,
    APIURL,
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    apiGet,
    toggleWidgetTheme,
    getEChartsTooltipTheme,
    getHeaderPinStateClass,
    getHeaderPinButtonHTML,
    setupHeaderPin
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface ExchangeChartSeries {
    name: string;
    values: number[];
}

interface ExchangeChartData {
    categories: string[];
    series: ExchangeChartSeries[];
}

interface ExchangeMarketAnalysisConfig extends WidgetConfig {
    assetType?: string;
    selectedExchange?: string;
    tradingStatus?: string;
}

const SEGMENT_COLORS = ['#7a00ff', '#4c00d6', '#33D17A', '#FFB020', '#FF6B6B', '#00B4D8', '#9B59B6', '#E67E22'];

export class TraderverseExchangeMarketAnalysis {
    private container: HTMLElement;
    private config: ExchangeMarketAnalysisConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private apiData: ExchangeChartData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private filters = {
        assetType: 'Stocks Only',
        selectedExchange: 'NASDAQ',
        tradingStatus: 'Actively Trading Only'
    };

    constructor(config: ExchangeMarketAnalysisConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<ExchangeMarketAnalysisConfig> {
        const symbols = this.fetchSymbols();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            assetType: 'Stocks Only',
            selectedExchange: 'NASDAQ',
            tradingStatus: 'Actively Trading Only'
        };
    }

    private applyConfigFilters(): void {
        if (this.config.assetType) this.filters.assetType = this.config.assetType;
        if (this.config.selectedExchange) this.filters.selectedExchange = this.config.selectedExchange;
        if (this.config.tradingStatus) this.filters.tradingStatus = this.config.tradingStatus;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private renderAssetTypeOptions(): string {
        const options = ['Stocks Only', 'ETFs Only', 'Mutual Funds Only', 'All'];
        return options.map(option => `<option value="${option}"${option === this.filters.assetType ? ' selected' : ''}>${option}</option>`).join('');
    }

    private renderExchangeOptions(): string {
        const options = ['NASDAQ', 'NYSE', 'AMEX', 'NASDAQCM', 'OTC'];
        return options.map(option => `<option value="${option}"${option === this.filters.selectedExchange ? ' selected' : ''}>${option}</option>`).join('');
    }

    private renderTradingStatusOptions(): string {
        const options = ['Actively Trading Only', 'All', 'Not Actively Trading'];
        return options.map(option => `<option value="${option}"${option === this.filters.tradingStatus ? ' selected' : ''}>${option}</option>`).join('');
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        this.setupEventListeners();
        await this.fetchData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Exchange Market Analysis</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow" data-testing-id="options-row">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                    <div class="trader-sm-card" id="selected-symbol">
                        ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                    </div>
                    <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Asset Type</label>
                    <select class="asset-type-select">${this.renderAssetTypeOptions()}</select>
                  </div>
                  <div class="item">
                    <label>Exchange</label>
                    <select class="exchange-select">${this.renderExchangeOptions()}</select>
                  </div>
                  <div class="item">
                    <label>Trading Status</label>
                    <select class="trading-status-select">${this.renderTradingStatusOptions()}</select>
                  </div>
                </div>
                <div class="item">
                    <div class="legendsCollection" id="custom-legends"></div>
                </div>
            </div>
            <div class="chartDv cryptoCompany" data-testing-id="chart-container">
                <div id="revenueProductSegmentsMarimekkoContainer"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }

        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private setupEventListeners(): void {
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        if (filtersBox) {
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const assetTypeSelect = this.shadowRoot.querySelector('.asset-type-select') as HTMLSelectElement;
        const exchangeSelect = this.shadowRoot.querySelector('.exchange-select') as HTMLSelectElement;
        const tradingStatusSelect = this.shadowRoot.querySelector('.trading-status-select') as HTMLSelectElement;

        const applyFiltersAndFetch = () => {
            this.filters.assetType = assetTypeSelect?.value || this.filters.assetType;
            this.filters.selectedExchange = exchangeSelect?.value || this.filters.selectedExchange;
            this.filters.tradingStatus = tradingStatusSelect?.value || this.filters.tradingStatus;
            this.config.assetType = this.filters.assetType;
            this.config.selectedExchange = this.filters.selectedExchange;
            this.config.tradingStatus = this.filters.tradingStatus;
            this.fetchData(this.config.symbol!);
        };

        assetTypeSelect?.addEventListener('change', applyFiltersAndFetch);
        exchangeSelect?.addEventListener('change', applyFiltersAndFetch);
        tradingStatusSelect?.addEventListener('change', applyFiltersAndFetch);

        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private toNumber(value: any): number | null {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
            const parsed = Number(value.replace(/[^0-9.-]/g, ''));
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    }

    private normalizeApiResponse(raw: any): ExchangeChartData | null {
        if (!raw || typeof raw !== 'object') return null;

        const payload = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
            ? raw.data
            : raw?.result && typeof raw.result === 'object' && !Array.isArray(raw.result)
                ? raw.result
                : raw;

        const sectorProportion = Array.isArray(payload?.sector_proportion)
            ? payload.sector_proportion
            : Array.isArray(raw?.sector_proportion)
                ? raw.sector_proportion
                : [];

        if (sectorProportion.length) {
            const categories = sectorProportion.map((item: any) => item?.sector || item?.name || item?.category || 'Unknown');
            const values = sectorProportion.map((item: any) => {
                const percentageValue = this.toNumber(item?.percentage);
                const value = this.toNumber(item?.value);
                const countValue = this.toNumber(item?.count);
                return percentageValue ?? value ?? countValue ?? 0;
            });
            return {
                categories,
                series: [{ name: 'Sector Share', values }]
            };
        }

        const extractItems = (source: any): Array<{ name: string; value: number }> => {
            if (!source) return [];
            if (Array.isArray(source)) {
                return source
                    .map((item: any) => {
                        if (!item || typeof item !== 'object') return null;
                        const name = this.findFirstString(item, ['name', 'label', 'title', 'category', 'sector', 'symbol', 'ticker']);
                        if (!name) return null;
                        const value = this.findFirstNumber(item, ['value', 'metric', 'count', 'score', 'percentage', 'change', 'marketCap', 'price']);
                        return value !== null ? { name, value } : null;
                    })
                    .filter(Boolean) as Array<{ name: string; value: number }>;
            }
            if (typeof source === 'object') {
                return Object.entries(source as Record<string, unknown>)
                    .map(([name, value]) => {
                        const numericValue = this.toNumber(value);
                        return numericValue !== null ? { name, value: numericValue } : null;
                    })
                    .filter(Boolean) as Array<{ name: string; value: number }>;
            }
            return [];
        };

        const directItems = extractItems(raw);
        if (directItems.length) {
            return {
                categories: directItems.map(item => item.name),
                series: [{ name: 'Value', values: directItems.map(item => item.value) }]
            };
        }

        const dataNode = raw?.data ?? raw?.result ?? raw?.items ?? raw?.results ?? raw?.rows;
        if (dataNode) {
            if (Array.isArray(dataNode)) {
                const items = extractItems(dataNode);
                if (items.length) {
                    return {
                        categories: items.map(item => item.name),
                        series: [{ name: 'Value', values: items.map(item => item.value) }]
                    };
                }
            } else if (typeof dataNode === 'object') {
                const categories = this.extractStringArray(dataNode?.categories ?? dataNode?.labels ?? dataNode?.xAxis ?? dataNode?.names);
                if (categories.length) {
                    const values = this.extractNumericArray(dataNode?.values ?? dataNode?.series?.[0]?.values ?? dataNode?.data);
                    if (values.length) {
                        return {
                            categories,
                            series: [{ name: dataNode?.series?.[0]?.name || 'Value', values }]
                        };
                    }
                }

                const seriesItems = Array.isArray(dataNode?.series)
                    ? dataNode.series.map((series: any) => ({ name: series?.name || 'Series', values: this.extractNumericArray(series?.values ?? series?.data) }))
                    : [];
                if (seriesItems.length && seriesItems.every((series:any) => series.values.length)) {
                    const categoriesFromSeries = this.extractStringArray(dataNode?.categories ?? dataNode?.labels ?? dataNode?.xAxis ?? dataNode?.names);
                    return {
                        categories: categoriesFromSeries.length ? categoriesFromSeries : Array.from({ length: Math.max(...seriesItems.map((series:any) => series.values.length)) }, (_, index) => `Item ${index + 1}`),
                        series: seriesItems
                    };
                }
            }
        }

        const objectEntries = Object.entries(raw as Record<string, unknown>);
        if (objectEntries.length) {
            const numericEntries = objectEntries
                .map(([name, value]) => {
                    const numericValue = this.toNumber(value);
                    return numericValue !== null ? { name, value: numericValue } : null;
                })
                .filter(Boolean) as Array<{ name: string; value: number }>;
            if (numericEntries.length) {
                return {
                    categories: numericEntries.map(item => item.name),
                    series: [{ name: 'Value', values: numericEntries.map(item => item.value) }]
                };
            }
        }

        return null;
    }

    private findFirstString(source: Record<string, any>, keys: string[]): string | null {
        for (const key of keys) {
            const value = source?.[key];
            if (typeof value === 'string' && value.trim()) return value;
        }
        return null;
    }

    private findFirstNumber(source: Record<string, any>, keys: string[]): number | null {
        for (const key of keys) {
            const value = this.toNumber(source?.[key]);
            if (value !== null) return value;
        }
        return null;
    }

    private extractStringArray(source: any): string[] {
        if (Array.isArray(source)) {
            return source.filter((item: any) => typeof item === 'string' && item.trim()).map((item: any) => String(item));
        }
        if (typeof source === 'object' && source) {
            return Object.keys(source);
        }
        return [];
    }

    private extractNumericArray(source: any): number[] {
        if (Array.isArray(source)) {
            return source.map(value => this.toNumber(value)).filter((value): value is number => value !== null);
        }
        return [];
    }

    private bindApiResponse(raw: any): boolean {
        const data = this.normalizeApiResponse(raw);
        if (!data?.categories?.length || !data.series?.length) {
            this.apiData = null;
            return false;
        }

        this.apiData = data;
        return true;
    }

    private async fetchData(symbol: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
        if (widget) widget.classList.add('loading');

        try {
            const response = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/exchange/market-analysis`,
                query: {
                    assetType: this.filters.assetType,
                    selectedExchange: this.filters.selectedExchange,
                    tradingStatus: this.filters.tradingStatus
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const fallbackText = await response.text();
                throw new Error(fallbackText || `API error: ${response.status}`);
            }
            const json = await response.json();
            if (!json || typeof json !== 'object') {
                throw new Error('Failed to fetch exchange market analysis');
            }

            if (!this.bindApiResponse(json)) {
                throw new Error('No exchange market analysis data available');
            }

            this.updateDisplay();
            this.emit('dataUpdate', {
                symbol,
                assetType: this.filters.assetType,
                selectedExchange: this.filters.selectedExchange,
                tradingStatus: this.filters.tradingStatus,
                data: this.apiData
            });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.apiData = null;
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.renderCustomLegends();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
        if (this.apiData?.categories?.length) {
            chartDv?.setAttribute('data-testing-id', 'revenue-product-segments-marimekko-chartdv');
        } else {
            chartDv?.removeAttribute('data-testing-id');
        }

        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }

        if (!this.apiData?.categories?.length || !this.apiData.series?.length) {
            const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
            if (container) container.innerHTML = '<div class="noData"><p>No exchange market analysis data available.</p></div>';
            if (this.currentChart) {
                try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
                this.currentChart = null;
            }
            this.renderCustomLegends();
            return;
        }

        const chartEl = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoChart') as HTMLElement | null;
        if (this.currentChart && chartEl) {
            this.drawChart();
            return;
        }

        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
        if (!container) return;

        container.innerHTML = `<div id="revenueProductSegmentsMarimekkoChart" class="chart coverDv" data-testing-id="revenue-product-segments-marimekko-chart"></div>`;
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoChart') as HTMLElement;
        if (!container || !this.apiData?.categories?.length || !this.apiData.series?.length || !echarts) return;

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#736D9D';
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm').trim() || '12px';

        if (!this.currentChart) {
            this.currentChart = echarts.init(container);
        }

        const categoryCount = Math.max(this.apiData.categories.length, 1);
        const chartWidth = container.clientWidth || 320;
        const labelWidth = Math.max(36, Math.floor((chartWidth - 52) / categoryCount) - 4);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const items = Array.isArray(params) ? params : [params];
                    const index = items[0]?.dataIndex ?? 0;
                    const category = this.apiData?.categories?.[index] ?? items[0]?.name ?? '';
                    const lines = items.map((item: any) => `${item.marker || ''}${item.seriesName}: ${item.value}`);
                    return `<div>${category}</div>${lines.join('<br/>')}`;
                }
            },
            legend: {
                show: this.apiData.series.length > 1,
                data: this.apiData.series.map(series => series.name),
                textStyle: { color: textColor, fontSize: 11 }
            },
            grid: { left: 40, right: 12, top: 26, bottom: 40, containLabel: true },
            xAxis: {
                type: 'category',
                data: this.apiData.categories,
                axisLabel: {
                    color: textColor,
                    fontSize: 10,
                    interval: 0,
                    rotate: 0,
                    width: labelWidth,
                    overflow: 'truncate',
                    ellipsis: '...'
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color')
                    }
                },
                axisLabel: { color: textColor, fontSize }
            },
            series: this.apiData.series.map((series, index) => ({
                name: series.name,
                type: 'bar',
                data: series.values,
                barMaxWidth: 44,
                itemStyle: { color: SEGMENT_COLORS[index % SEGMENT_COLORS.length] },
                emphasis: { focus: 'series' }
            }))
        };

        this.currentChart.setOption(option, true);
        this.currentChart.resize();
        this.renderCustomLegends();
    }

    private renderCustomLegends(): void {
        const container = this.shadowRoot.querySelector('#custom-legends') as HTMLElement;
        if (!container) return;

        container.innerHTML = '';
        const series = this.apiData?.series || [];
        if (!series.length) return;

        series.forEach((entry, index) => {
            const el = document.createElement('span');
            el.className = 'toggle-series active';
            el.setAttribute('data-symbol', entry.name);
            el.style.backgroundColor = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
            el.title = entry.name;
            container.appendChild(el);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (id: string) => this.fetchData(id),
            (type: WidgetEventType, data: any) => this.emit(type, data)
        );
    }

    public updateWithResponse(resp: any): void {
        if (!this.bindApiResponse(resp)) return;
        this.updateDisplay();
        this.emit('dataUpdate', this.apiData);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseExchangeMarketAnalysis: typeof TraderverseExchangeMarketAnalysis; } }
if (typeof window !== 'undefined') { window.TraderverseExchangeMarketAnalysis = TraderverseExchangeMarketAnalysis; }
